import { useEffect, useRef, useState } from "react";
import { FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import API_URL from "../../config/api";
import Header, { Footer } from "../HomePage/header";
import { useNotification } from "../Notification/notification";
import "./product.css";

export default function ProductPage() {
  const [productsPerPage, setProductsPerPage] = useState(9);
  const [showFilter, setShowFilter] = useState(false);

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const { notify } = useNotification();

  const [isCompact, setIsCompact] = useState(false);
  const lastScrollY = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setProductsPerPage(8);
      } else {
        setProductsPerPage(9);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/products`)
      .then((res) => res.json())
      .then((data) => {
        console.log("API DATA:", data);

        const productsArray = Array.isArray(data) ? data : [];

        const mapped = productsArray.map((p) => ({
          id: p.id,
          slug: p.slug,
          productName: p.name || "No Name",
          productImage: p.image || "https://via.placeholder.com/150",
          productPrice: Number(p.price) || 0,
          productCategory: p.category || "General",
          productSubCategory: p.subcategory || "",
          productStock: p.stock || 0,
          productBrand: p.brand || "",
          productDescription: p.description || "",
        }));

        setProducts(mapped);
        setFilteredProducts(mapped);

        const grouped = [...new Set(mapped.map((p) => p.productCategory))].map(
          (cat) => ({
            category: cat,
            subcategories: [
              ...new Set(
                mapped
                  .filter((p) => p.productCategory === cat)
                  .map((p) => p.productSubCategory)
                  .filter(Boolean),
              ),
            ],
          }),
        );

        setCategories(grouped);
      })
      .catch((err) => {
        console.log("API ERROR:", err);
        setProducts([]);
        setFilteredProducts([]);
      });
  }, []);

  function addToCart(product) {
    let cart = JSON.parse(localStorage.getItem("cart")) || [];

    const existing = cart.find((item) => item.id === product.id);

    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    notify(product.productName + " added to cart", "success");
  }

  function filterProducts(subcategories, price) {
    let filtered = products;

    if (subcategories.length > 0) {
      filtered = filtered.filter((p) =>
        subcategories.includes(p.productSubCategory),
      );
    }

    if (price) {
      const [min, max] = price.split("-").map(Number);

      filtered = filtered.filter((p) => {
        if (max) {
          return p.productPrice >= min && p.productPrice <= max;
        }
        return p.productPrice >= min;
      });
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }

  function handleSubcategoryChange(value) {
    let updated;

    if (selectedSubcategories.includes(value)) {
      updated = selectedSubcategories.filter((v) => v !== value);
    } else {
      updated = [...selectedSubcategories, value];
    }

    setSelectedSubcategories(updated);
    filterProducts(updated, selectedPrice);
  }

  function handlePriceChange(value) {
    setSelectedPrice(value);
    filterProducts(selectedSubcategories, value);
  }

  function addToWishlist(product) {
    let wishlist = JSON.parse(localStorage.getItem("wishlist")) || [];

    const exists = wishlist.some((item) => item.id === product.id);

    if (!exists) {
      wishlist.push(product);
      localStorage.setItem("wishlist", JSON.stringify(wishlist));
      notify(product.productName + " added to wishlist", "success");
    } else {
      notify(product.productName + " already in wishlist", "info");
    }
  }

  const start = (currentPage - 1) * productsPerPage;
  const end = start + productsPerPage;

  const paginatedProducts = filteredProducts.slice(start, end);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  useEffect(() => {
    if (showFilter) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      const scrollY = -parseInt(document.body.style.top || "0");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    }

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [showFilter]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll > lastScrollY.current && currentScroll > 50) {
        setIsCompact(true);
      } else {
        setIsCompact(false);
      }

      lastScrollY.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div>
      <Header />

      {/* CATEGORY BUTTONS */}
      <div className="product-categories">
        <div className="category-buttons-wrapper">
          <button
            className="home-button"
            onClick={() => setFilteredProducts(products)}
          >
            All
          </button>

          <div className="category-buttons">
            {categories.map((cat, index) => (
              <button
                key={index}
                className="category-button"
                onClick={() => {
                  const filtered = products.filter(
                    (p) => p.productCategory === cat.category,
                  );
                  setFilteredProducts(filtered);
                  setCurrentPage(1);
                }}
              >
                {cat.category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* PRODUCT AREA */}
      <div className="product-container-wrapper">
        <button className="filter-toggle" onClick={() => setShowFilter(true)}>
          <FaFilter />
        </button>

        <div className={`filter-category ${showFilter ? "active" : ""}`}>
          <button className="close-filter" onClick={() => setShowFilter(false)}>
            ✕
          </button>

          <h3>Select Category</h3>
          {categories.map((cat, index) => (
            <div key={index}>
              <h4>{cat.category}</h4>
              {(cat.subcategories || []).map((sub, i) => (
                <div key={i}>
                  <input
                    type="checkbox"
                    onChange={() => handleSubcategoryChange(sub)}
                    checked={selectedSubcategories.includes(sub)}
                  />
                  <label>{sub}</label>
                </div>
              ))}
            </div>
          ))}

          <h4>Price</h4>
          {["50-100", "100-200", "200-300", "300-500"].map((range) => (
            <div key={range}>
              <input
                type="radio"
                name="price"
                onChange={() => handlePriceChange(range)}
                checked={selectedPrice === range}
              />
              Rs {range.replace("-", " - Rs ")}
            </div>
          ))}
        </div>

        {/* PRODUCTS */}
        <div className="productContainer">
          {paginatedProducts.map((product) => (
            <div
              key={product.id}
              className="productBox"
              onClick={() => navigate(`/product/${product.slug}/${product.id}`)}
              style={{ cursor: "pointer" }}
            >
              <img
                className="productImage"
                src={product.productImage}
                alt={product.productName}
              />
              <h2>{product.productName}</h2>
              <p>{product.productCategory}</p>
              <p>Rs {product.productPrice}</p>

              <div className="productActions">
                <button
                  className="icon-btn wishlist-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    addToWishlist(product);
                  }}
                >
                  ❤️
                </button>

                <button
                  className="icon-btn view-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    notify(`Viewing ${product.name}`, "info");
                  }}
                  title="View Product"
                >
                  👁
                </button>
              </div>

              <button
                className="addTocartButton"
                onClick={(e) => {
                  e.stopPropagation();
                  addToCart(product);
                }}
              >
                Add to Cart
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* PAGINATION */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Prev
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            disabled={currentPage === i + 1}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
      </div>

      <Footer />
    </div>
  );
}
